import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { BonanzaPromoStatus } from '@/helpers/bonanzaPromo';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { useCommonStore } from '@/store';

import { ScBonanzaPromoSection } from './styled';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';

const MAX_PROGRESS = 100;

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile,
  onLinkClick,
}) => {
  const { t } = useTranslation('bonanzaPromo');

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const { bonanzaReward, bonanzaStaticData, userBonanzaGroup } = useCommonStore(
    useShallow((state) => ({
      bonanzaReward: state.bonanzaReward,
      bonanzaStaticData: state.bonanzaStaticData,
      userBonanzaGroup: state.userBonanzaGroup,
    }))
  );

  const isPromoComplete = useMemo(
    () => bonanzaStaticData.progressPercent === MAX_PROGRESS,
    [bonanzaStaticData.progressPercent]
  );

  const { navigate } = useNavigation();
  const {
    images: {
      arrowDownGrayIcon: { component: ArrowDownGrayIcon },
    },
  } = useTheme();

  const progressBarCompleteText = useMemo(() => {
    if (userBonanzaGroup === BonanzaPromoStatus.BONUS_ISSUED)
      return t('bonanzaPromo:buttons.claimBonus');
  }, [t, userBonanzaGroup]);

  const isLockedPromo = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.PROMO_LOCKED,
    [userBonanzaGroup]
  );

  return (
    <ScBonanzaPromoSection
      showFullInfo={isFullInfoOpen}
      onClick={() => isMobile && toggleFullInfo()}
    >
      <div className="info">
        <div className="level-info">
          {isLockedPromo ? (
            <div className="text">
              <span>
                <b>{t('bonanzaPromo:returnToStartFresh')}</b>
              </span>
            </div>
          ) : (
            <div className="text">
              <span>
                <b>{t('bonanzaPromo:yourProgress')}:</b>
                {bonanzaStaticData.progressPercent}%
              </span>
              <span>
                <b>{t('bonanzaPromo:yourReward')}:</b>
                {bonanzaReward}
              </span>
            </div>
          )}
          {isMobile && (
            <div className="addons">
              <ImageComponent
                src={bonanzaStaticData.label}
                width={44}
                height={44}
                alt="logo"
                objectFit="contain"
              />
              <ArrowDownGrayIcon />
            </div>
          )}
        </div>
      </div>

      {!isLockedPromo && (
        <ProgressBar
          value={bonanzaStaticData.progressPercent}
          mode={
            isPromoComplete
              ? ProgressBarMode.GREEN_WHITE
              : ProgressBarMode.PRIMARY_GRAY
          }
          showValue={ValuePosition.CENTER}
          height={24}
          borderRadius="24px"
          borderRadiusInner={isPromoComplete ? '24px' : '24px 0 0 24px'}
          withUnits
          showTo={false}
          completedText={progressBarCompleteText}
        />
      )}

      {(!isMobile || isFullInfoOpen) && (
        <Button
          grayTransparentBg
          wide
          small
          onClick={() => {
            onLinkClick && onLinkClick();
            navigate('/bonanza', true);
          }}
        >
          {t('common:details')}
        </Button>
      )}
    </ScBonanzaPromoSection>
  );
};
